import { useState, useEffect, useCallback } from "react";
import api, { BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link } from "react-router-dom";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Save, Link as LinkIcon, Key, MapPin, Copy, Check, BookOpen, Workflow, ArrowRight, AlertCircle, Sparkles, Eye, EyeOff, Activity, Wrench, Building2, PhoneCall, QrCode, Terminal } from "lucide-react";

function GhlOAuthConnect({ user }) {
  const { t } = useTranslation();
  const [status, setStatus] = useState(null);
  const [connecting, setConnecting] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/my-business/ghl");
      setStatus(data);
    } catch {
      setStatus({ connected: false });
    }
  }, []);

  useEffect(() => {
    refresh();
    // When the user comes back from the GHL OAuth tab, re-check the connection status
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refresh]);

  const connect = async () => {
    setConnecting(true);
    try {
      const { data } = await api.get(`/ghl/oauth/install-url?business_id=${user.business_id}`);
      window.open(data.install_url, "_blank", "noopener,noreferrer");
      toast.success(t("stg.toastAuthorize"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("stg.toastConnectFailed"));
    } finally {
      setConnecting(false);
    }
  };

  const connected = !!status?.connected;
  return (
    <Card className={`border-2 ${connected ? "border-green-300 bg-green-50" : "border-amber-300 bg-amber-50"}`} data-testid="ghl-oauth-connect-card">
      <CardHeader>
        <CardTitle className="text-lg font-['Manrope'] flex items-center gap-2">
          <LinkIcon className={`w-5 h-5 ${connected ? "text-green-600" : "text-amber-600"}`} /> {t("stg.oauthTitle")}
          {connected && <Badge className="bg-green-100 text-green-700 border-green-200">{t("stg.connectedBadge")}</Badge>}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {!connected ? (
          <>
            <p className="text-sm text-amber-800">
              <strong>{t("stg.oauthWarnTitle")}</strong> {t("stg.oauthWarnText")}
            </p>
            <ol className="text-sm text-muted-foreground list-decimal list-inside space-y-1">
              <li>{t("stg.oauthStep1")}</li>
              <li>{t("stg.oauthStep2")}</li>
              <li>{t("stg.oauthStep3")}</li>
            </ol>
          </>
        ) : (
          <p className="text-sm text-green-800">
            {t("stg.oauthLinkedVia")} <strong>{status.via === "oauth" ? "OAuth (Marketplace)" : t("stg.viaToken")}</strong>
            {status.ghl_location_id ? <> — Location <code className="text-xs bg-green-100 px-1 py-0.5 rounded">{status.ghl_location_id}</code></> : null}.
            {" "}{t("stg.syncActive")}
          </p>
        )}
        <div className="flex flex-wrap items-center gap-3">
          <Button
            onClick={connect}
            disabled={connecting}
            variant={connected ? "outline" : "default"}
            className={connected ? "" : "bg-[#FFD60A] text-black hover:bg-[#FFD60A]/90"}
            data-testid="ghl-oauth-connect-button"
          >
            <LinkIcon className="w-4 h-4 mr-2" />
            {connecting ? t("stg.oauthOpening") : connected ? t("stg.oauthReconnect") : t("stg.oauthConnectBtn")}
          </Button>
          {!connected && (
            <p className="text-xs text-muted-foreground">
              {t("stg.marketplaceNote")}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function Settings() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [settings, setSettings] = useState({
    ghl_api_key: "", ghl_location_id: "",
    openai_api_key: "", anthropic_api_key: "", ai_model: "",
    response_field_key: "salesbot_response", goals_field_key: "salesbot_goals_accomplished"
  });
  const [envStatus, setEnvStatus] = useState({ openai: false, ghl: false, ghl_oauth: false });
  const [oauthStatus, setOauthStatus] = useState({ connected: false, mode: "none", location_id: "" });
  const [doctor, setDoctor] = useState({ loading: false, data: null });
  const [oauthConnecting, setOauthConnecting] = useState(false);
  const [billingEnforced, setBillingEnforced] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showOpenAIKey, setShowOpenAIKey] = useState(false);
  const [showGHLKey, setShowGHLKey] = useState(false);
  
  // For admin business selector
  const [businesses, setBusinesses] = useState([]);
  const [selectedBusinessId, setSelectedBusinessId] = useState("");

  const webhookUrl = `${BACKEND_URL}/api/webhooks/ghl`;

  const isAdmin = user?.role === "admin";
  // Use selected business for admin, or user's assigned business
  const businessId = isAdmin ? selectedBusinessId : (user?.business_id || null);

  const fetchOAuthStatus = async () => {
    if (!businessId) return;
    try {
      const { data } = await api.get(`/ghl/oauth/status?business_id=${businessId}`);
      setOauthStatus({
        connected: data.connected || false,
        mode: data.connected ? "oauth" : "none",
        location_id: data.location_id || settings.ghl_location_id || "",
      });
    } catch {
      setOauthStatus({ connected: false, mode: "none", location_id: settings.ghl_location_id || "" });
    }
  };

  useEffect(() => {
    const fetch = async () => {
      try {
        const { data } = await api.get("/settings");
        setSettings({
          ghl_api_key: data.ghl_api_key || "",
          ghl_location_id: data.ghl_location_id || "",
          openai_api_key: data.openai_api_key || "",
          anthropic_api_key: data.anthropic_api_key || "",
          ai_model: data.ai_model || "",
          response_field_key: data.response_field_key || "salesbot_response",
          goals_field_key: data.goals_field_key || "salesbot_goals_accomplished",
        });
        setEnvStatus({
          openai: data.openai_env_configured || false,
          ghl: data.ghl_env_configured || false,
          ghl_oauth: data.ghl_oauth_env_configured || false,
        });
        
        // Only direct (landing) customers self-link their CRM — whitelabel clients never see the prompt
        if (user?.role !== "admin" && !(user?.email || "").endsWith("@leadbot.embed")) {
          api.get("/billing/status").then(({ data }) => {
            setBillingEnforced(Boolean(data?.enforced));
          }).catch(() => null);
        }

        // Load businesses for admin selector
        if (user?.role === "admin") {
          try {
            const bizRes = await api.get("/businesses");
            setBusinesses(bizRes.data || []);
            // Auto-select first business if available
            if (bizRes.data?.length > 0 && !selectedBusinessId) {
              setSelectedBusinessId(bizRes.data[0].id);
            }
          } catch (e) {
            console.error("Failed to load businesses");
          }
        }
      } catch (e) {
        console.error("Failed to load settings");
      } finally {
        setLoading(false);
      }
    };
    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role]);

  useEffect(() => {
    fetchOAuthStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      // Only send non-masked values
      const dataToSend = { ...settings };
      if (dataToSend.openai_api_key && dataToSend.openai_api_key.startsWith("***")) {
        delete dataToSend.openai_api_key;
      }
      if (dataToSend.ghl_api_key && dataToSend.ghl_api_key.startsWith("***")) {
        delete dataToSend.ghl_api_key;
      }
      if (dataToSend.anthropic_api_key && dataToSend.anthropic_api_key.startsWith("***")) {
        delete dataToSend.anthropic_api_key;
      }
      await api.put("/settings", dataToSend);
      toast.success(t("stg.toastSaved"));
    } catch (e) {
      toast.error(t("stg.toastSaveFailed"));
    } finally {
      setSaving(false);
    }
  };

  const copyWebhook = () => {
    navigator.clipboard.writeText(webhookUrl);
    setCopied(true);
    toast.success(t("stg.webhookCopied"));
    setTimeout(() => setCopied(false), 2000);
  };

  const runDoctor = async () => {
    setDoctor({ loading: true, data: null });
    try {
      const url = businessId ? `/ghl/diagnostics?business_id=${businessId}` : "/ghl/diagnostics";
      const { data } = await api.get(url);
      setDoctor({ loading: false, data });
      if (data.overall_ok) {
        toast.success(t("stg.toastDoctorOk"));
      } else {
        toast.error(t("stg.toastDoctorIssues"));
      }
    } catch (e) {
      setDoctor({ loading: false, data: null });
      toast.error(e?.response?.data?.detail || t("stg.toastDoctorFailed"));
    }
  };

  const connectOAuth = async () => {
    setOauthConnecting(true);
    try {
      const url = businessId ? `/ghl/oauth/install-url?business_id=${businessId}` : "/ghl/oauth/install-url";
      const { data } = await api.get(url);
      window.open(data.install_url, "_blank", "noopener,noreferrer");
      toast.success(t("stg.toastOauthOpening"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("stg.toastOauthFailed"));
    } finally {
      setOauthConnecting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const isEmbed = (user?.email || "").endsWith("@leadbot.embed");

  // Whitelabel: embed (restricted) users get a clean view — no platform/GHL internals
  if (isEmbed) {
    return (
      <div data-testid="settings-page" className="p-8 animate-fade-in max-w-2xl">
        <div className="mb-8">
          <h1 className="text-3xl font-['Manrope'] font-extrabold text-foreground">{t("stg.title")}</h1>
        </div>
        <Card className="bg-white border-gray-200">
          <CardContent className="py-10 text-center space-y-3">
            <Check className="w-10 h-10 text-primary mx-auto" />
            <h3 className="text-lg font-semibold text-foreground">{t("stg.embedTitle")}</h3>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">
              {t("stg.embedDesc1")}
            </p>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">
              {t("stg.embedDesc2")} <strong>{t("stg.embedPath")}</strong>.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div data-testid="settings-page" className="p-8 animate-fade-in max-w-4xl">
      <div className="mb-8">
        <h1 className="text-3xl font-['Manrope'] font-extrabold text-foreground">{t("stg.title")}</h1>
        <p className="text-muted-foreground mt-1">{t("stg.subtitle")}</p>
      </div>

      {!isAdmin && billingEnforced && (
        <div className="mb-6">
          <GhlOAuthConnect user={user} />
        </div>
      )}

      <Tabs defaultValue="credentials" className="space-y-6">
        <TabsList className="bg-secondary border border-border">
          <TabsTrigger value="credentials" data-testid="tab-credentials">{t("stg.tabCredentials")}</TabsTrigger>
          <TabsTrigger value="setup-guide" data-testid="tab-setup-guide">{t("stg.tabGuide")}</TabsTrigger>
        </TabsList>

        <TabsContent value="credentials" className="space-y-6">
          {/* OpenAI API Key */}
          <Card className="bg-white border-gray-200">
            <CardHeader>
              <CardTitle className="text-lg font-['Manrope'] flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-[#10a37f]" /> OpenAI API Key
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {envStatus.openai && (
                <div className="p-3 rounded-lg bg-primary/10 border border-primary/30">
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-[#10a37f]" />
                    <p className="text-sm text-[#10a37f]">
                      {t("stg.envConfigured")}
                    </p>
                  </div>
                </div>
              )}
              <div>
                <Label className="text-sm text-muted-foreground mb-1.5 block">{t("stg.globalOpenai")}</Label>
                <div className="flex gap-2">
                  <Input 
                    data-testid="openai-api-key-input" 
                    type={showOpenAIKey ? "text" : "password"} 
                    value={settings.openai_api_key}
                    onChange={(e) => setSettings({ ...settings, openai_api_key: e.target.value })}
                    placeholder={envStatus.openai ? t("stg.envKeyPh") : "sk-proj-..."}
                    className="bg-secondary/50 border-transparent focus:border-[#10a37f] font-mono" 
                  />
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={() => setShowOpenAIKey(!showOpenAIKey)}
                    className="border-border shrink-0"
                  >
                    {showOpenAIKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground/60 mt-1">
                  {t("stg.getKeyFrom")} <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-[#10a37f] hover:underline">platform.openai.com</a>
                </p>
              </div>
              <div>
                <Label className="text-sm text-muted-foreground mb-1.5 block">{t("stg.claudeKeyLabel")}</Label>
                <Input
                  data-testid="anthropic-api-key-input"
                  type="password"
                  value={settings.anthropic_api_key}
                  onChange={(e) => setSettings({ ...settings, anthropic_api_key: e.target.value })}
                  placeholder={t("stg.claudeKeyPh")}
                  className="bg-secondary/50 border-transparent focus:border-primary font-mono"
                />
              </div>
              <div>
                <Label className="text-sm text-muted-foreground mb-1.5 block">{t("stg.aiModelLabel")}</Label>
                <Input
                  data-testid="ai-model-input"
                  value={settings.ai_model}
                  onChange={(e) => setSettings({ ...settings, ai_model: e.target.value })}
                  placeholder={t("stg.aiModelPh")}
                  className="bg-secondary/50 border-transparent focus:border-primary font-mono"
                />
              </div>
              <div className="p-3 rounded-lg bg-secondary/30">
                <p className="text-xs text-muted-foreground">
                  <strong className="text-foreground">{t("stg.priorityLabel")}</strong> {t("stg.priorityText")}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Webhook URL */}
          <Card className="bg-white border-gray-200">
            <CardHeader>
              <CardTitle className="text-lg font-['Manrope'] flex items-center gap-2">
                <LinkIcon className="w-5 h-5 text-primary" /> Webhook URL
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-3">
                {t("stg.webhookDesc")}
              </p>
              <div className="flex gap-2">
                <Input readOnly value={webhookUrl} className="bg-secondary/50 border-transparent font-mono text-sm" data-testid="webhook-url-input" />
                <Button data-testid="copy-webhook-btn" onClick={copyWebhook} variant="outline" className="border-border shrink-0">
                  {copied ? <Check className="w-4 h-4 text-primary" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* GHL Credentials (legacy PIT mode - hidden when OAuth is connected or for embed/SSO users) */}
          {!oauthStatus.connected && !user?.is_embed_user && (
          <Card className="bg-white border-gray-200">
            <CardHeader>
              <CardTitle className="text-lg font-['Manrope'] flex items-center gap-2">
                <Key className="w-5 h-5 text-[#FFD60A]" /> {t("stg.ghlCredsTitle")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {envStatus.ghl && (
                <div className="p-3 rounded-lg bg-[#FFD60A]/10 border border-[#FFD60A]/30">
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-[#FFD60A]" />
                    <p className="text-sm text-[#FFD60A]">
                      {t("stg.ghlEnvConfigured")}
                    </p>
                  </div>
                </div>
              )}
              <div>
                <Label className="text-sm text-muted-foreground mb-1.5 block">{t("stg.pitLabel")}</Label>
                <div className="flex gap-2">
                  <Input 
                    data-testid="ghl-api-key-input" 
                    type={showGHLKey ? "text" : "password"} 
                    value={settings.ghl_api_key}
                    onChange={(e) => setSettings({ ...settings, ghl_api_key: e.target.value })}
                    placeholder="pit-..."
                    className="bg-secondary/50 border-transparent focus:border-primary font-mono" 
                  />
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={() => setShowGHLKey(!showGHLKey)}
                    className="border-border shrink-0"
                  >
                    {showGHLKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground/60 mt-1">Agency Settings → Integrations → Private Integrations</p>
              </div>
              <div>
                <Label className="text-sm text-muted-foreground mb-1.5 block">{t("stg.locationLabel")}</Label>
                <Input data-testid="ghl-location-id-input" value={settings.ghl_location_id}
                  onChange={(e) => setSettings({ ...settings, ghl_location_id: e.target.value })}
                  placeholder={t("stg.locationPh")}
                  className="bg-secondary/50 border-transparent focus:border-primary font-mono" />
              </div>
            </CardContent>
          </Card>
          )}

          {/* GHL Connection Doctor */}
          <Card className="bg-white border-gray-200" data-testid="ghl-connection-doctor-card">
            <CardHeader>
              <CardTitle className="text-lg font-['Manrope'] flex items-center gap-2">
                <Activity className="w-5 h-5 text-[#0A84FF]" /> {t("stg.doctorTitle")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-3 rounded-lg bg-[#0A84FF]/5 border border-[#0A84FF]/20 text-xs text-[#0A84FF]">
                {t("stg.doctorDesc")}
              </div>

              {/* Business Selector for Admins */}
              {isAdmin && (
                <div className="p-3 rounded-lg bg-yellow-50 border border-yellow-200">
                  <Label className="text-sm font-medium flex items-center gap-2 mb-2">
                    <Building2 className="w-4 h-4 text-yellow-600" /> {t("stg.selectBizLabel")}
                  </Label>
                  <Select value={selectedBusinessId} onValueChange={(v) => {
                    setSelectedBusinessId(v);
                    setDoctor({ loading: false, data: null });
                    setOauthStatus({ connected: false, mode: "none", location_id: "" });
                  }}>
                    <SelectTrigger className="bg-white border-yellow-300" data-testid="business-selector">
                      <SelectValue placeholder={t("stg.selectBizPh")} />
                    </SelectTrigger>
                    <SelectContent>
                      {businesses.map(biz => (
                        <SelectItem key={biz.id} value={biz.id}>
                          {biz.name} {biz.ghl_location_id ? `(${biz.ghl_location_id.substring(0, 8)}...)` : t("stg.sinGhl")}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {!selectedBusinessId && (
                    <p className="text-xs text-yellow-700 mt-2">
                      {t("stg.selectBizHint")}
                    </p>
                  )}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Button
                  data-testid="ghl-run-doctor-button"
                  onClick={runDoctor}
                  disabled={doctor.loading || (isAdmin && !selectedBusinessId)}
                  variant="outline"
                  className="border-[#0A84FF]/30 text-[#0A84FF]"
                >
                  <Wrench className="w-4 h-4 mr-2" /> {doctor.loading ? t("stg.doctorRunning") : t("stg.runDoctor")}
                </Button>
                <Button
                  data-testid="ghl-connect-oauth-button"
                  onClick={connectOAuth}
                  disabled={oauthConnecting || (isAdmin && !selectedBusinessId)}
                  className="bg-[#FFD60A] text-black hover:bg-[#FFD60A]/90"
                >
                  <LinkIcon className="w-4 h-4 mr-2" /> {oauthConnecting ? t("stg.oauthGenerating") : t("stg.oauthConnect")}
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="p-3 rounded-lg bg-secondary/30 flex items-center justify-between" data-testid="ghl-oauth-status-row">
                  <span className="text-sm">{t("stg.oauthStatus")}</span>
                  <Badge variant="outline" className={oauthStatus.connected ? "border-primary text-primary" : "border-destructive text-destructive"}>
                    {oauthStatus.connected ? t("stg.connected") : t("stg.notConnected")}
                  </Badge>
                </div>
                <div className="p-3 rounded-lg bg-secondary/30 flex items-center justify-between" data-testid="ghl-oauth-env-status-row">
                  <span className="text-sm">OAuth Env</span>
                  <Badge variant="outline" className={envStatus.ghl_oauth ? "border-primary text-primary" : "border-destructive text-destructive"}>
                    {envStatus.ghl_oauth ? t("stg.configured") : t("stg.missing")}
                  </Badge>
                </div>
              </div>

              {doctor.data?.checks?.length > 0 && (
                <div className="space-y-2" data-testid="ghl-doctor-results">
                  {doctor.data.checks.map((check) => (
                    <div key={check.id} className="p-3 rounded-lg bg-secondary/30 flex items-center justify-between gap-3" data-testid={`ghl-doctor-check-${check.id}`}>
                      <div>
                        <p className="text-sm font-medium text-foreground">{check.label}</p>
                        <p className="text-xs text-muted-foreground break-all">{check.detail}</p>
                      </div>
                      <Badge variant="outline" className={check.ok ? "border-primary text-primary" : "border-destructive text-destructive"}>
                        {check.ok ? "OK" : "FAIL"}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Custom Field Keys */}
          <Card className="bg-white border-gray-200">
            <CardHeader>
              <CardTitle className="text-lg font-['Manrope'] flex items-center gap-2">
                <MapPin className="w-5 h-5 text-[#0A84FF]" /> {t("stg.customFieldsTitle")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-3 rounded-lg bg-[#FFD60A]/5 border border-[#FFD60A]/20">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-[#FFD60A] mt-0.5 shrink-0" />
                  <p className="text-xs text-[#FFD60A]/80">
                    {t("stg.customFieldsWarn")}
                  </p>
                </div>
              </div>
              <div>
                <Label className="text-sm text-muted-foreground mb-1.5 block">{t("stg.botResponseLabel")}</Label>
                <Input value={settings.response_field_key}
                  onChange={(e) => setSettings({ ...settings, response_field_key: e.target.value })}
                  placeholder="salesbot_response"
                  className="bg-secondary/50 border-transparent focus:border-primary font-mono"
                  data-testid="response-field-key-input" />
                <p className="text-xs text-muted-foreground/60 mt-1">{t("stg.botResponseHint")}</p>
              </div>
              <div>
                <Label className="text-sm text-muted-foreground mb-1.5 block">{t("stg.goalsLabel")}</Label>
                <Input value={settings.goals_field_key}
                  onChange={(e) => setSettings({ ...settings, goals_field_key: e.target.value })}
                  placeholder="salesbot_goals_accomplished"
                  className="bg-secondary/50 border-transparent focus:border-primary font-mono"
                  data-testid="goals-field-key-input" />
                <p className="text-xs text-muted-foreground/60 mt-1">{t("stg.goalsHint")}</p>
              </div>
            </CardContent>
          </Card>

          {/* Status */}
          <Card className="bg-white border-gray-200">
            <CardHeader><CardTitle className="text-lg font-['Manrope']">{t("stg.statusTitle")}</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-3">
                {[
                  { label: "OpenAI API Key", ok: !!settings.openai_api_key || envStatus.openai },
                  { label: "GHL API Key", ok: !!settings.ghl_api_key || envStatus.ghl || oauthStatus.connected },
                  { label: "Location ID", ok: !!(settings.ghl_location_id || oauthStatus.location_id) },
                  { label: "Webhook Endpoint", ok: true },
                  { label: t("stg.labelResponse"), ok: !!settings.response_field_key },
                ].map(item => (
                  <div key={item.label} className="flex items-center justify-between p-3 rounded-lg bg-secondary/30">
                    <span className="text-sm text-foreground">{item.label}</span>
                    <Badge variant="outline" className={item.ok ? "border-primary text-primary" : "border-destructive text-destructive"}>
                      {item.ok ? t("stg.configured") : t("stg.statusNotSet")}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button data-testid="save-settings-btn" onClick={handleSave} disabled={saving}
              className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_20px_rgba(0,230,118,0.3)] active:scale-95 transition-transform duration-150">
              <Save className="w-4 h-4 mr-2" />{saving ? t("stg.saving") : t("stg.saveSettings")}
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="setup-guide">
          <div className="space-y-6">
            {/* Paso 1: conectar la subcuenta */}
            <Card className="bg-white border-gray-200">
              <CardHeader>
                <CardTitle className="text-lg font-['Manrope'] flex items-center gap-2">
                  <LinkIcon className="w-5 h-5 text-primary" />
                  <span>{t("stg.step1Title")}</span>
                  <span className="text-primary">{t("stg.step1Name")}</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <StepItem n="1" text={t("stg.g1s1")} />
                <StepItem n="2" text={t("stg.g1s2")} />
                <StepItem n="3" text={t("stg.g1s3")} />
              </CardContent>
            </Card>

            {/* Paso 2: proveedor de telefonía */}
            <Card className="bg-white border-amber-300">
              <CardHeader>
                <CardTitle className="text-lg font-['Manrope'] flex items-center gap-2">
                  <PhoneCall className="w-5 h-5 text-amber-500" />
                  <span>{t("stg.step2Title")}</span>
                  <span className="text-amber-600">{t("stg.step2Name")}</span>
                  <Badge className="bg-amber-100 text-amber-700 border-amber-300 text-xs">{t("stg.required")}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <StepItem n="1" text={t("stg.g2s1")} />
                <StepItem n="2" text={t("stg.g2s2")} />
                <StepItem n="3" text={t("stg.g2s3")} />
                <StepItem n="4" text={t("stg.g2s4")} />
                <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 space-y-1">
                  <p className="text-xs text-amber-800">
                    <strong>{t("stg.g2Warn1Strong")}</strong> {t("stg.g2Warn1Text")}
                  </p>
                  <p className="text-xs text-amber-800">
                    {t("stg.g2Warn2")}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Paso 3: conectar WhatsApp */}
            <Card className="bg-white border-gray-200">
              <CardHeader>
                <CardTitle className="text-lg font-['Manrope'] flex items-center gap-2">
                  <QrCode className="w-5 h-5 text-[#00E676]" />
                  <span>{t("stg.step3Title")}</span>
                  <span className="text-[#00C864]">{t("stg.step3Name")}</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <StepItem n="1" text={t("stg.g3s1")} />
                <StepItem n="2" text={t("stg.g3s2")} />
                <StepItem n="3" text={t("stg.g3s3")} />
                <p className="text-xs text-muted-foreground ml-9">
                  {t("stg.g3NotePre")} <code className="font-mono bg-secondary px-1 rounded">#switch|N</code>{t("stg.g3NotePost")}
                </p>
              </CardContent>
            </Card>

            {/* Paso 4: comandos y menús */}
            <Card className="bg-white border-gray-200">
              <CardHeader>
                <CardTitle className="text-lg font-['Manrope'] flex items-center gap-2">
                  <Terminal className="w-5 h-5 text-[#BF5AF2]" />
                  <span>{t("stg.step4Title")}</span>
                  <span className="text-[#BF5AF2]">{t("stg.step4Name")}</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  {t("stg.g4Text")}
                </p>
                <Link to="/whatsapp/docs">
                  <Button variant="outline" size="sm">
                    <BookOpen className="w-4 h-4 mr-2" /> {t("stg.g4Btn")}
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Campos personalizados */}
            <Card className="bg-white border-gray-200">
              <CardHeader>
                <CardTitle className="text-lg font-['Manrope'] flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-primary" /> {t("stg.cfTitle")}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-muted-foreground">{t("stg.cfDescPre")} <strong className="text-foreground">{t("stg.cfDescStrong")}</strong> {t("stg.cfDescPost")}</p>
                <div className="p-3 rounded-lg bg-primary/5 border border-primary/20 mb-2">
                  <p className="text-xs text-primary/80">
                    <strong className="text-primary">{t("stg.cfBotStrong")}</strong> {t("stg.cfBotTextPre")} <code className="bg-primary/10 px-1 rounded">ai_off</code>. {t("stg.cfBotTextPost")}
                  </p>
                </div>
                <div className="space-y-2">
                  {[
                    { name: "salesbot_response", desc: t("stg.cf1desc") },
                    { name: "salesbot_goals_accomplished", desc: t("stg.cf2desc") },
                    { name: "last_communication_type", desc: t("stg.cf3desc") },
                  ].map(f => (
                    <div key={f.name} className="flex items-center gap-3 p-3 rounded-lg bg-secondary/30">
                      <code className="text-xs text-primary font-mono bg-primary/10 px-2 py-1 rounded">{f.name}</code>
                      <span className="text-xs text-muted-foreground">{f.desc}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Automatización avanzada (legado): workflows del CRM para el bot IA clásico */}
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="legacy" className="border rounded-lg px-4 bg-white">
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center gap-2 text-left">
                    <Workflow className="w-5 h-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium text-foreground">{t("stg.legacyTitle")}</p>
                      <p className="text-xs text-muted-foreground font-normal">
                        {t("stg.legacyDesc")}
                      </p>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="space-y-6 pt-2">

            {/* Workflow 1 */}
            <Card className="bg-white border-gray-200">
              <CardHeader>
                <CardTitle className="text-lg font-['Manrope'] flex items-center gap-2">
                  <Workflow className="w-5 h-5 text-[#0A84FF]" />
                  <span>{t("stg.wf1Title")}</span>
                  <span className="text-[#0A84FF]">Set Last Communication Type</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <StepItem n="1" text={t("stg.wf1s1")} />
                <StepItem n="2" text={t("stg.wf1s2")} />
                <StepItem n="3" text={t("stg.wf1s3")} />
                <StepItem n="4" text={t("stg.wf1s4")} />
                <StepItem n="5" text={t("stg.wf1s5")} />
              </CardContent>
            </Card>

            {/* Workflow 2 */}
            <Card className="bg-white border-gray-200">
              <CardHeader>
                <CardTitle className="text-lg font-['Manrope'] flex items-center gap-2">
                  <Workflow className="w-5 h-5 text-primary" />
                  <span>{t("stg.wf2Title")}</span>
                  <span className="text-primary">Process Message</span>
                  <Badge variant="outline" className="border-border text-muted-foreground text-xs">{t("stg.legacyBadge")}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <StepItem n="1" text={t("stg.wf2s1")} />
                <StepItem n="2" text={t("stg.wf2s2")} />
                <StepItem n="3" text={t("stg.wf2s3")} />
                <div className="ml-8 p-4 rounded-lg bg-secondary/30 font-mono text-xs space-y-2">
                  <p className="text-muted-foreground">URL: <span className="text-primary break-all">{webhookUrl}</span></p>
                  <p className="text-muted-foreground">Method: <span className="text-foreground">POST</span></p>
                  <p className="text-muted-foreground mt-2">Custom Data:</p>
                  <div className="space-y-1 pl-2">
                    <p><span className="text-[#FFD60A]">@lead_name</span> → <span className="text-foreground">{"{{contact.first_name}}"}</span></p>
                    <p><span className="text-[#FFD60A]">id</span> → <span className="text-foreground">{"{{contact.id}}"}</span></p>
                    <p><span className="text-[#FFD60A]">goals_finished</span> → <span className="text-foreground">{"{{contact.salesbot_goals_accomplished}}"}</span></p>
                    <p><span className="text-[#FFD60A]">message</span> → <span className="text-foreground">{"{{message.body}}"}</span></p>
                  </div>
                </div>
                <StepItem n="4" text={t("stg.wf2s4")} />
              </CardContent>
            </Card>

            {/* Workflow 3 */}
            <Card className="bg-white border-gray-200">
              <CardHeader>
                <CardTitle className="text-lg font-['Manrope'] flex items-center gap-2">
                  <Workflow className="w-5 h-5 text-[#BF5AF2]" />
                  <span>{t("stg.wf3Title")}</span>
                  <span className="text-[#BF5AF2]">Send Message</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <StepItem n="1" text={t("stg.wf3s1")} />
                <StepItem n="2" text={t("stg.wf3s2")} />
                <StepItem n="3" text={t("stg.wf3s3")} />
                <StepItem n="4" text={t("stg.wf3s4")} />
                <div className="ml-8 p-3 rounded-lg bg-secondary/30">
                  <code className="text-sm text-[#BF5AF2] font-mono">{"{{contact.salesbot_response}}"}</code>
                </div>
              </CardContent>
            </Card>

            {/* Workflow 4 - Follow Up */}
            <Card className="bg-white border-gray-200">
              <CardHeader>
                <CardTitle className="text-lg font-['Manrope'] flex items-center gap-2">
                  <Workflow className="w-5 h-5 text-[#FFD60A]" />
                  <span>{t("stg.wf4Title")}</span>
                  <span className="text-[#FFD60A]">Follow Up 1 Day</span>
                  <Badge variant="outline" className="border-border text-muted-foreground text-xs">{t("stg.optionalBadge")}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <StepItem n="1" text={t("stg.wf4s1")} />
                <StepItem n="2" text={t("stg.wf4s2")} />
                <StepItem n="3" text={t("stg.wf4s3")} />
                <div className="ml-8 p-4 rounded-lg bg-secondary/30 font-mono text-xs space-y-1">
                  <p><span className="text-[#FFD60A]">@lead_name</span> → <span className="text-foreground">{"{{contact.first_name}}"}</span></p>
                  <p><span className="text-[#FFD60A]">followup</span> → <span className="text-foreground">true</span></p>
                  <p><span className="text-[#FFD60A]">id</span> → <span className="text-foreground">{"{{contact.id}}"}</span></p>
                  <p><span className="text-[#FFD60A]">goals_finished</span> → <span className="text-foreground">{"{{contact.salesbot_goals_accomplished}}"}</span></p>
                </div>
              </CardContent>
            </Card>

            {/* Flow Diagram */}
            <Card className="bg-white border-gray-200">
              <CardHeader>
                <CardTitle className="text-lg font-['Manrope']">{t("stg.flowTitle")}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  {[
                    { text: t("stg.flow1"), color: "#0A84FF" },
                    { text: t("stg.flow2"), color: "#FFD60A" },
                    { text: t("stg.flow3"), color: "#00E676" },
                    { text: t("stg.flow4"), color: "#BF5AF2" },
                    { text: t("stg.flow5"), color: "#00E676" },
                    { text: t("stg.flow6"), color: "#FFD60A" },
                    { text: t("stg.flow7"), color: "#0A84FF" },
                  ].map((step, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className="px-3 py-1.5 rounded-lg text-xs font-medium border" style={{ borderColor: `${step.color}40`, color: step.color, backgroundColor: `${step.color}10` }}>
                        {step.text}
                      </span>
                      {i < 6 && <ArrowRight className="w-3.5 h-3.5 text-muted-foreground" />}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

const StepItem = ({ n, text }) => (
  <div className="flex items-start gap-3">
    <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">{n}</span>
    <p className="text-sm text-muted-foreground">{text}</p>
  </div>
);
